import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';

import {
    HALF_H, GOAL_W, GOAL_W_2D, GOAL_LINE_Y, PASS_DURATION, SHOOT_DURATION, PUCK_R, PUCK_H,
} from './three/constants';
import PlayerMotion, { MotionCurve } from './three/Motion';
import Arena from './three/Arena';
import PlayerFactory from './three/PlayerModel';
import SkaterAnimator, { ACTION_TIMING } from './three/SkatingAnimator';
import CameraDirector from './three/CameraDirector';
import PostFX, { detectTier } from './three/PostFX';
import { makeRadialTexture } from './three/textures';

const TAU = Math.PI * 2;
const clamp  = (v, a, b) => Math.min(b, Math.max(a, v));
const smooth = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); };
const wrapAngle = a => { while (a > Math.PI) a -= TAU; while (a < -Math.PI) a += TAU; return a; };
// Heading convention: rotation.z = h makes the player face (-sin h, cos h)
const headingOf = (dx, dy) => Math.atan2(-dx, dy);

const GOAL_POS   = { x: 0, y: GOAL_LINE_Y };   // shots always target the top goal (same as 2D)
const SLAP_DIST  = 9;
const TRAIL_N    = 26;

// ─── Shaders ──────────────────────────────────────────────────────────────────
const flowVertex = /* glsl */`
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

// Movement path: dim behind the player, bright flowing dashes ahead
const pathFragment = /* glsl */`
    uniform vec3 uColor; uniform float uProgress; uniform float uTime; uniform float uLen; uniform float uOpacity;
    varying vec2 vUv;
    void main() {
        float ahead = step(uProgress, vUv.x);
        float dash = step(0.45, fract(vUv.x * uLen / 0.7 - uTime * 0.9));
        float a = mix(0.10, 0.30 + 0.35 * dash, ahead) * uOpacity;
        gl_FragColor = vec4(uColor * (1.0 + 0.6 * ahead), a);
    }`;

// Pass / shot arc: animated chevrons flowing toward the target
const arrowFragment = /* glsl */`
    uniform vec3 uColor; uniform float uTime; uniform float uLen; uniform float uOpacity;
    varying vec2 vUv;
    void main() {
        float f = fract(vUv.x * uLen / 0.9 - uTime * 2.2);
        float dash = smoothstep(0.0, 0.15, f) * (1.0 - smoothstep(0.55, 0.7, f));
        gl_FragColor = vec4(uColor * 1.6, (0.25 + 0.75 * dash) * uOpacity);
    }`;

// ─── TacticEngine3D ───────────────────────────────────────────────────────────
// Public API (used by PublicCanvas3D):
//   new TacticEngine3D(container, { onCameraModeChange })
//   update(tactic, currentTime)   setCameraMode('tv'|'free'|'top')   resetCamera()
//   setTopView(bool)              destroy()
export default class TacticEngine3D {
    constructor(container, options = {}) {
        this.container = container;
        this._onCameraModeChange = options.onCameraModeChange || (() => {});
        this._tier = detectTier();

        this._players = [];
        this._paths   = [];
        this._arrows  = [];
        this._actions = [];
        this._tactic  = null;
        this._time    = 0;
        this._prevT   = 0;
        this._animId  = null;

        this._puckOffset  = new THREE.Vector3();
        this._puckVisual  = new THREE.Vector3(0, 0, PUCK_H / 2);
        this._puckVel     = new THREE.Vector3();
        this._puckKey     = '';
        this._trail       = [];
        this._fps         = { frames: 0, time: 0, warm: 0 };
        this._spotScale   = 1;

        this._init();
        this._startLoop();
    }

    // ─── Init ─────────────────────────────────────────────────────────────────
    _init() {
        const w = this.container.clientWidth  || 1;
        const h = this.container.clientHeight || 1;

        this.renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
        this.renderer.setSize(w, h, false);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.05;
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        const el = this.renderer.domElement;
        el.style.width = '100%';
        el.style.height = '100%';
        el.style.display = 'block';
        el.style.touchAction = 'none';
        this.container.appendChild(el);

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x050b16);
        this.scene.fog = new THREE.FogExp2(0x070f1c, 0.011);

        const pmrem = new THREE.PMREMGenerator(this.renderer);
        this._envTex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        pmrem.dispose();
        this.scene.environment = this._envTex;
        this.scene.environmentIntensity = 0.38;

        // Z-up: field in the XY plane. camera.up must be set before OrbitControls.
        this.camera = new THREE.PerspectiveCamera(34, w / h, 0.1, 300);
        this.camera.up.set(0, 0, 1);

        this.arena = new Arena(this.scene, {
            tier: this._tier,
            aniso: this.renderer.capabilities.getMaxAnisotropy(),
        });
        this.arena.setTitle('TÁCTICA', '');
        this.factory = new PlayerFactory();
        this._buildPuck();
        this._buildOverlays();

        this.director = new CameraDirector(this.camera, el, {
            onModeChange: mode => this._onCameraModeChange(mode),
        });
        this.fx = new PostFX(this.renderer, this.scene, this.camera, this._tier);
        this.fx.setSize(w, h);

        this.clock = new THREE.Clock();
        this._resizeObserver = new ResizeObserver(() => this._onResize());
        this._resizeObserver.observe(this.container);
    }

    // ─── Puck, halo, trail, possession ring ───────────────────────────────────
    _buildPuck() {
        const r = PUCK_R, hh = PUCK_H / 2, ch = 0.014;
        const profile = [[0, -hh], [r - ch, -hh], [r, -hh + ch], [r, hh - ch], [r - ch, hh], [0, hh]]
            .map(([x, y]) => new THREE.Vector2(x, y));
        const geo = new THREE.LatheGeometry(profile, 32);
        geo.rotateX(Math.PI / 2);
        const puck = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0x0c0c0e, roughness: 0.5, metalness: 0.05 }));
        puck.castShadow = true;
        this.scene.add(puck);
        this._puck = puck;

        this._glowTex = makeRadialTexture([[0, 'rgba(255,255,255,1)'], [0.35, 'rgba(255,255,255,0.35)'], [1, 'rgba(255,255,255,0)']]);
        const halo = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({ map: this._glowTex, color: 0x9fd4ff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false })
        );
        halo.position.z = 0.01;
        halo.renderOrder = 3;
        this.scene.add(halo);
        this._halo = halo;

        // Trail ribbon (updated every frame)
        const tg = new THREE.BufferGeometry();
        tg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(TRAIL_N * 2 * 3), 3));
        tg.setAttribute('color', new THREE.BufferAttribute(new Float32Array(TRAIL_N * 2 * 3), 3));
        const idx = [];
        for (let i = 0; i < TRAIL_N - 1; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
        tg.setIndex(idx);
        const trail = new THREE.Mesh(tg, new THREE.MeshBasicMaterial({
            vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        }));
        trail.frustumCulled = false;
        trail.renderOrder = 4;
        this.scene.add(trail);
        this._trailMesh = trail;
    }

    _buildOverlays() {
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(0.62, 0.74, 48),
            new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false })
        );
        ring.position.z = 0.012;
        ring.renderOrder = 3;
        this.scene.add(ring);
        this._ring = ring;
        this._ringOpacity = 0;
    }

    // ─── Tactic (re)build ─────────────────────────────────────────────────────
    _rebuild(tactic) {
        this._players.forEach(p => { this.scene.remove(p.root); p.dispose(); });
        this._players = [];
        this._clearPathsAndArrows();
        this._actions = [];
        if (!tactic || !tactic.players) return;

        this.arena.setTitle(tactic.name || 'TÁCTICA', tactic.type === 'offense' ? 'ATAQUE' : tactic.type === 'defense' ? 'DEFENSA' : '');

        tactic.players.forEach(player => {
            const { root, rig, dispose } = this.factory.create(player);
            this.scene.add(root);
            const motion = new PlayerMotion(player.keyframes);
            const p0 = this._visualPos(player, motion, 0);
            root.position.set(p0.x, p0.y, 0);
            this._players.push({
                player, root, rig, motion, dispose,
                anim: new SkaterAnimator(rig),
                heading: player.role === 'goalie'
                    ? headingOf(-p0.x, (p0.y > 0 ? -1 : 1) * 10)
                    : headingOf(0, 1),
                sample: {},
            });
        });

        this._actions = (tactic.actions || []).slice().sort((a, b) => a.t - b.t);
        this._buildPaths();
        this._buildArrows();
        this._puckKey = '';
        this._puckOffset.set(0, 0, 0);
        this._trail.length = 0;
        this._snapHeadings = true;
    }

    _clearPathsAndArrows() {
        this._paths.forEach(p => { this.scene.remove(p.mesh); p.mesh.geometry.dispose(); p.mesh.material.dispose(); });
        this._arrows.forEach(a => {
            this.scene.remove(a.mesh, a.tip);
            a.mesh.geometry.dispose(); a.mesh.material.dispose();
            a.tip.geometry.dispose(); a.tip.material.dispose();
        });
        this._paths = [];
        this._arrows = [];
    }

    _buildPaths() {
        this._players.forEach(p => {
            const m = p.motion;
            if (m.isStatic || m.t1 - m.t0 < 0.05) return;
            const curve = new MotionCurve(m, 0.02);
            const segs = Math.max(48, Math.ceil(m.totalArc * 6));
            const geo = new THREE.TubeGeometry(curve, segs, 0.06, 6, false);
            geo.scale(1, 1, 0.15);
            const mat = new THREE.ShaderMaterial({
                uniforms: {
                    uColor: { value: new THREE.Color(p.player.color || '#ffffff') },
                    uProgress: { value: 0 }, uTime: { value: 0 },
                    uLen: { value: m.totalArc }, uOpacity: { value: 1 },
                },
                vertexShader: flowVertex, fragmentShader: pathFragment,
                transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
            });
            const mesh = new THREE.Mesh(geo, mat);
            mesh.renderOrder = 2;
            this.scene.add(mesh);
            this._paths.push({ mesh, motion: m });
        });
    }

    _buildArrows() {
        const players = this._players;
        this._actions.forEach(action => {
            const from = players[action.fromPlayerIndex];
            if (!from) return;
            const fp = from.motion.position(action.t);
            let tp;
            if (action.type === 'pass' && action.toPlayerIndex != null) {
                const to = players[action.toPlayerIndex];
                if (!to) return;
                tp = to.motion.position(action.t + PASS_DURATION);
            } else if (action.type === 'shoot') {
                tp = { ...GOAL_POS };
            } else return;

            const a3 = new THREE.Vector3(fp.x, fp.y, 0.05);
            const b3 = new THREE.Vector3(tp.x, tp.y, action.type === 'shoot' ? 0.5 : 0.05);
            const len = a3.distanceTo(b3);
            const mid = a3.clone().lerp(b3, 0.5);
            mid.z = 0.2 + len * (action.type === 'shoot' ? 0.06 : 0.03);
            const curve = new THREE.QuadraticBezierCurve3(a3, mid, b3);
            const geo = new THREE.TubeGeometry(curve, 48, 0.055, 8, false);
            const color = new THREE.Color(action.type === 'pass' ? 0x3dff8c : 0xff3b3b);
            const mat = new THREE.ShaderMaterial({
                uniforms: { uColor: { value: color }, uTime: { value: 0 }, uLen: { value: len }, uOpacity: { value: 0.5 } },
                vertexShader: flowVertex, fragmentShader: arrowFragment,
                transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
            });
            const mesh = new THREE.Mesh(geo, mat);
            mesh.renderOrder = 2;

            const tip = new THREE.Mesh(
                new THREE.ConeGeometry(0.16, 0.42, 14),
                new THREE.MeshBasicMaterial({ color: color.clone().multiplyScalar(1.6), transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false })
            );
            const tan = curve.getTangent(1);
            tip.position.copy(b3).addScaledVector(tan, -0.1);
            tip.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), tan);
            this.scene.add(mesh, tip);
            this._arrows.push({ mesh, tip, action });
        });
    }

    // Goalies placed on/behind the goal line are drawn standing in the crease
    _visualPos(player, motion, t, out) {
        const s = motion.sample(t, out || {});
        if (player.role === 'goalie' && Math.abs(s.x) < GOAL_W_2D / 2 + 0.7 && Math.abs(s.y) > GOAL_LINE_Y - 0.25) {
            s.y = Math.sign(s.y) * (GOAL_LINE_Y - 0.55);
        }
        return s;
    }

    _posAt(i, t) {
        const p = this._players[i];
        return p ? this._visualPos(p.player, p.motion, t) : { x: 0, y: 0 };
    }

    // ─── Puck possession (same rules as TacticEngine.js) ──────────────────────
    _ballState(t) {
        const players = this._players;
        if (players.length === 0) return { possessor: -1, flight: null, rest: null };
        const start = this._tactic && players[this._tactic.puckStart] ? this._tactic.puckStart : 0;
        let possessor = start, flight = null, rest = null;
        for (const action of this._actions) {
            if (action.t > t) break;
            if (!players[action.fromPlayerIndex]) continue;
            const elapsed = t - action.t;
            if (action.type === 'pass' && action.toPlayerIndex != null) {
                if (!players[action.toPlayerIndex]) continue;
                rest = null;
                if (elapsed < PASS_DURATION) { possessor = -1; flight = { action, alpha: elapsed / PASS_DURATION }; }
                else { possessor = action.toPlayerIndex; flight = null; }
            } else if (action.type === 'shoot') {
                possessor = -1;
                if (elapsed < SHOOT_DURATION) { flight = { action, alpha: elapsed / SHOOT_DURATION }; rest = null; }
                else { flight = null; rest = { action, age: elapsed - SHOOT_DURATION }; }
            }
        }
        return { possessor, flight, rest };
    }

    _shotAim(action) {
        const fp = this._posAt(action.fromPlayerIndex, action.t);
        const far = fp.x > 0.4 ? -1 : fp.x < -0.4 ? 1 : (action.t * 7) % 2 > 1 ? 1 : -1;
        const dist = Math.hypot(fp.x - GOAL_POS.x, fp.y - GOAL_POS.y);
        return {
            from: fp,
            x: far * (GOAL_W / 2 - 0.55),
            z: dist > SLAP_DIST ? 0.75 : 0.42,
            slap: dist > SLAP_DIST,
        };
    }

    // Which choreographed action (if any) each player is performing at time t
    _playerActions(t) {
        const res = this._players.map(() => null);
        const consider = (i, cand) => {
            if (i == null || !this._players[i]) return;
            if (!res[i] || Math.abs(cand.s) < Math.abs(res[i].s)) res[i] = cand;
        };
        for (const a of this._actions) {
            if (a.type === 'pass' && a.toPlayerIndex != null) {
                const tm = ACTION_TIMING.pass;
                const tau = t - a.t;
                if (tau >= -tm.windup && tau <= tm.follow) {
                    const to = this._posAt(a.toPlayerIndex, a.t + PASS_DURATION);
                    consider(a.fromPlayerIndex, { kind: 'pass', s: tau < 0 ? tau / tm.windup : tau / tm.follow, target: to });
                }
                const rm = ACTION_TIMING.receive;
                const tau2 = t - (a.t + PASS_DURATION);
                if (tau2 >= -rm.windup && tau2 <= rm.follow) {
                    consider(a.toPlayerIndex, { kind: 'receive', s: tau2 < 0 ? tau2 / rm.windup : tau2 / rm.follow, target: null });
                }
            } else if (a.type === 'shoot') {
                const aim = this._shotAim(a);
                const kind = aim.slap ? 'slap' : 'wrist';
                const tm = ACTION_TIMING[kind];
                const tau = t - a.t;
                if (tau >= -tm.windup && tau <= tm.follow) {
                    consider(a.fromPlayerIndex, { kind, s: tau < 0 ? tau / tm.windup : tau / tm.follow, target: { x: aim.x, y: GOAL_POS.y } });
                }
            }
        }
        return res;
    }

    // ─── Per-frame simulation ─────────────────────────────────────────────────
    _simulate(dt, clock) {
        const t = this._time;
        const jumped = Math.abs(t - this._prevT) > 0.3 || t < this._prevT - 1e-3;
        this._prevT = t;
        const snap = jumped || this._snapHeadings;
        this._snapHeadings = false;

        const ball = this._ballState(t);
        const acts = this._playerActions(t);
        const puckPrev = this._puckVisual;

        // Goalie butterfly when a shot is coming at a goalie in the top net
        let butterflyT = 0;
        const shotAction = ball.flight && ball.flight.action.type === 'shoot' ? ball.flight : null;
        if (shotAction && shotAction.alpha > 0.25) butterflyT = 1;
        if (ball.rest && ball.rest.age < 0.8) butterflyT = 1;

        this._players.forEach((p, i) => {
            const s = this._visualPos(p.player, p.motion, t, p.sample);
            const v = Math.hypot(s.vx, s.vy);
            const isGoalie = p.player.role === 'goalie';
            const act = acts[i];
            const carrier = ball.possessor === i;

            // ── Facing ────────────────────────────────────────────────────────
            let target;
            const toPuck = headingOf(puckPrev.x - s.x, puckPrev.y - s.y);
            if (act && act.kind !== 'receive' && act.target) {
                // Shooter/passer turns side-on so the forehand faces the target
                target = headingOf(act.target.x - s.x, act.target.y - s.y) - 0.5;
            } else if (act && act.kind === 'receive') {
                target = toPuck - 0.35;
            } else if (isGoalie) {
                target = Math.hypot(puckPrev.x - s.x, puckPrev.y - s.y) > 0.4 ? toPuck : p.heading;
            } else if (carrier) {
                target = v > 1 ? headingOf(s.vx, s.vy) : headingOf(GOAL_POS.x - s.x, GOAL_POS.y - s.y) - 0.3;
            } else if (v > 1.3) {
                const move = headingOf(s.vx, s.vy);
                // Skate backwards to keep eyes on the puck when it is behind the direction of travel
                const behind = Math.abs(wrapAngle(toPuck - move)) > 2.0 && v < 7;
                target = behind ? move + Math.PI : move;
            } else {
                target = toPuck;
            }
            if (snap) p.heading = target;
            else {
                const d = wrapAngle(target - p.heading);
                const maxStep = 7.5 * dt;
                p.heading += clamp(d * (1 - Math.exp(-9 * dt)), -maxStep, maxStep);
            }
            p.heading = wrapAngle(p.heading);
            p.root.position.set(s.x, s.y, 0);
            p.root.rotation.set(0, 0, p.heading);

            // ── Animate ───────────────────────────────────────────────────────
            const accLong = v > 0.3 ? (s.vx * s.ax + s.vy * s.ay) / v : 0;
            const accLat  = v > 0.3 ? (s.vx * s.ay - s.vy * s.ax) / v : 0;
            let butterfly = 0;
            if (isGoalie && butterflyT > 0 && s.y > GOAL_LINE_Y - 4.5 && Math.abs(s.x) < 4) butterfly = butterflyT;

            p.anim.update({
                t, dt: snap ? 1 : dt, clock,
                vx: s.vx, vy: s.vy, speed: v, accLong, accLat,
                phase: p.motion.phaseAt(t), arc: p.motion.arcAt(t),
                puck: puckPrev, carrier,
                action: act ? { kind: act.kind, s: act.s } : null,
                butterfly,
            });

            // Badges grow in top view for legibility
            const top = this.director.mode === 'top';
            p.rig.badge.scale.set(top ? 1.5 : 0.78, top ? 0.56 : 0.29, 1);
        });

        this._updatePuck(dt, t, ball, jumped);
        this._updateOverlays(dt, t, clock, ball);
    }

    _updatePuck(dt, t, ball, jumped) {
        const target = new THREE.Vector3();
        let key, inFlight = false;
        const players = this._players;

        if (ball.possessor >= 0 && players[ball.possessor]) {
            target.copy(players[ball.possessor].anim.puckContact);
            target.z = PUCK_H / 2;
            key = `c${ball.possessor}`;
        } else if (ball.flight) {
            const { action, alpha } = ball.flight;
            inFlight = true;
            key = `f${this._actions.indexOf(action)}`;
            const fp = this._posAt(action.fromPlayerIndex, action.t);
            if (action.type === 'pass') {
                const tp = this._posAt(action.toPlayerIndex, action.t + PASS_DURATION);
                const dx = tp.x - fp.x, dy = tp.y - fp.y;
                const l = Math.hypot(dx, dy) || 1;
                const off = Math.min(0.7, l * 0.25);
                const ax = fp.x + dx / l * off, ay = fp.y + dy / l * off;
                const bx = tp.x - dx / l * off, by = tp.y - dy / l * off;
                target.set(ax + (bx - ax) * alpha, ay + (by - ay) * alpha, PUCK_H / 2);
            } else {
                const aim = this._shotAim(action);
                const dx = aim.x - fp.x, dy = GOAL_POS.y + 0.3 - fp.y;
                const l = Math.hypot(dx, dy) || 1;
                const ax = fp.x + dx / l * 0.7, ay = fp.y + dy / l * 0.7;
                const e = alpha;
                target.set(
                    ax + (aim.x - ax) * e,
                    ay + (GOAL_POS.y + 0.3 - ay) * e,
                    PUCK_H / 2 + (aim.z - PUCK_H / 2) * e + (aim.slap ? 0.35 : 0.25) * Math.sin(Math.PI * e)
                );
            }
        } else if (ball.rest) {
            const aim = this._shotAim(ball.rest.action);
            const fall = clamp(ball.rest.age / 0.35, 0, 1);
            target.set(aim.x * 0.85, GOAL_POS.y + 0.3 + 0.55 * Math.min(1, ball.rest.age * 4), aim.z + (PUCK_H / 2 - aim.z) * fall * fall);
            key = `r${this._actions.indexOf(ball.rest.action)}`;
        } else {
            target.set(0, 0, PUCK_H / 2);
            key = 'none';
        }

        // Hand-offs (pass release, reception) blend smoothly instead of popping
        if (jumped) this._puckOffset.set(0, 0, 0);
        else if (key !== this._puckKey) {
            this._puckOffset.subVectors(this._puckVisual, target);
            if (this._puckOffset.length() > 3) this._puckOffset.set(0, 0, 0);
        }
        this._puckKey = key;
        this._puckOffset.multiplyScalar(Math.exp(-dt * 16));

        const prev = this._puckVisual.clone();
        this._puckVisual.copy(target).add(this._puckOffset);
        if (dt > 0) this._puckVel.subVectors(this._puckVisual, prev).divideScalar(Math.max(dt, 1e-3));
        if (jumped) this._puckVel.set(0, 0, 0);

        this._puck.position.copy(this._puckVisual);
        const spd = Math.hypot(this._puckVel.x, this._puckVel.y);
        if (inFlight) this._puck.rotation.z += spd * dt * 2.5;
        this._halo.position.set(this._puckVisual.x, this._puckVisual.y, 0.01);
        const hs = this.director.mode === 'top' ? 1.6 : 0.95;
        this._halo.scale.set(hs, hs, 1);

        // Trail
        if (jumped) this._trail.length = 0;
        if (inFlight && spd > 3) this._trail.unshift({ p: this._puckVisual.clone(), life: 1 });
        this._trail.forEach(s => { s.life -= dt * 4; });
        while (this._trail.length && (this._trail.length > TRAIL_N || this._trail[this._trail.length - 1].life <= 0)) this._trail.pop();
        this._updateTrailMesh(ball.flight && ball.flight.action.type === 'shoot');
    }

    _updateTrailMesh(isShot) {
        const geo = this._trailMesh.geometry;
        const pos = geo.attributes.position.array;
        const col = geo.attributes.color.array;
        const pts = this._trail;
        const c = isShot ? [1.0, 0.45, 0.3] : [0.45, 0.85, 1.0];
        for (let i = 0; i < TRAIL_N; i++) {
            const s = pts[Math.min(i, pts.length - 1)];
            const n = pts[Math.min(i + 1, pts.length - 1)];
            if (!s) { pos.fill(0); col.fill(0); break; }
            let dx = n.p.x - s.p.x, dy = n.p.y - s.p.y;
            const l = Math.hypot(dx, dy) || 1;
            dx /= l; dy /= l;
            const k = i < pts.length ? 1 - i / TRAIL_N : 0;
            const wdt = PUCK_R * 0.9 * k;
            const a = k * k * Math.max(0, s.life) * 0.9;
            pos[i * 6]     = s.p.x - dy * wdt; pos[i * 6 + 1] = s.p.y + dx * wdt; pos[i * 6 + 2] = s.p.z;
            pos[i * 6 + 3] = s.p.x + dy * wdt; pos[i * 6 + 4] = s.p.y - dx * wdt; pos[i * 6 + 5] = s.p.z;
            for (let j = 0; j < 2; j++) {
                col[i * 6 + j * 3]     = c[0] * a;
                col[i * 6 + j * 3 + 1] = c[1] * a;
                col[i * 6 + j * 3 + 2] = c[2] * a;
            }
        }
        geo.attributes.position.needsUpdate = true;
        geo.attributes.color.needsUpdate = true;
    }

    _updateOverlays(dt, t, clock, ball) {
        // Possession ring under the puck carrier
        const carrier = ball.possessor >= 0 ? this._players[ball.possessor] : null;
        const targetOp = carrier ? 0.55 + 0.2 * Math.sin(clock * 5) : 0;
        this._ringOpacity += (targetOp - this._ringOpacity) * (1 - Math.exp(-8 * dt));
        this._ring.material.opacity = this._ringOpacity;
        if (carrier) {
            this._ring.position.set(carrier.root.position.x, carrier.root.position.y, 0.012);
            this._ring.material.color.set(carrier.player.color || '#ffffff');
        }

        this._paths.forEach(({ mesh, motion }) => {
            const u = mesh.material.uniforms;
            u.uProgress.value = clamp((t - motion.t0) / Math.max(1e-3, motion.t1 - motion.t0), 0, 1);
            u.uTime.value = clock;
        });
        this._arrows.forEach(({ mesh, tip, action }) => {
            const near = 1 - smooth(0.0, 0.6, Math.abs(t - action.t));
            const op = 0.32 + 0.6 * near;
            mesh.material.uniforms.uOpacity.value = op;
            mesh.material.uniforms.uTime.value = clock;
            tip.material.opacity = op;
        });

        // Goal celebration: lamp, net ripple, crowd
        let goal = null;
        if (ball.rest) {
            const age = ball.rest.age;
            const aim = this._shotAim(ball.rest.action);
            const strength = age < 2.6 ? 1 : 1 - smooth(2.6, 3.4, age);
            goal = { strength, impact: { x: aim.x, z: aim.z, age } };
        }
        this.arena.update(clock, goal);
    }

    // ─── Public API ───────────────────────────────────────────────────────────
    update(tactic, currentTime) {
        if (tactic !== this._tactic) {
            this._rebuild(tactic);
            this._tactic = tactic;
        }
        this._time = currentTime || 0;
    }

    setCameraMode(mode) { this.director.setMode(mode); }
    getCameraMode() { return this.director.mode; }
    resetCamera() { this.director.reset(); }
    setTopView(top) { this.director.setMode(top ? 'top' : 'free'); }

    // ─── Loop ─────────────────────────────────────────────────────────────────
    _startLoop() {
        const loop = () => {
            this._animId = requestAnimationFrame(loop);
            const dt = Math.min(this.clock.getDelta(), 0.1);
            const clock = this.clock.elapsedTime;

            if (this._tactic) this._simulate(dt, clock);
            else this.arena.update(clock, null);

            const pv = this._puckVisual, vel = this._puckVel;
            this.director.update(dt, { x: pv.x, y: clamp(pv.y, -HALF_H, HALF_H), vx: vel.x, vy: vel.y });
            this.arena.setOverheadVisible(!this.director.isSteep() && this.camera.aspect >= 1.1);
            // Thin the arena haze when looking straight down from high up
            const fogTarget = this.director.mode === 'top' ? 0.003 : 0.011;
            this.scene.fog.density += (fogTarget - this.scene.fog.density) * (1 - Math.exp(-4 * dt));
            this._spotScale += ((this.director.mode === 'top' ? 0.25 : 1) - this._spotScale) * (1 - Math.exp(-4 * dt));
            this.arena.setSpotScale(this._spotScale);
            this.fx.render(dt);
            this._watchPerformance(dt);
        };
        this._animId = requestAnimationFrame(loop);
    }

    // Drop to the low tier if the device can't hold ~40 fps
    _watchPerformance(dt) {
        const f = this._fps;
        f.warm += dt;
        if (f.warm < 3) return;
        f.frames++;
        f.time += dt;
        if (f.time < 2.5) return;
        const fps = f.frames / f.time;
        f.frames = 0; f.time = 0;
        if (fps < 38 && this._tier === 'high') {
            this._tier = 'low';
            this.fx.setTier('low');
            this.arena.setShadowQuality(1024);
        } else if (fps < 28 && this._tier === 'low') {
            this._tier = 'min';
            this.renderer.setPixelRatio(1);
            this.renderer.shadowMap.type = THREE.PCFShadowMap;
            const c = this.container;
            this.fx.setSize(c.clientWidth, c.clientHeight);
        }
    }

    _onResize() {
        const w = this.container.clientWidth;
        const h = this.container.clientHeight;
        if (w < 1 || h < 1) return;
        this.renderer.setSize(w, h, false);
        this.fx.setSize(w, h);
        this.director.resize(w, h);
    }

    destroy() {
        if (this._animId) cancelAnimationFrame(this._animId);
        if (this._resizeObserver) this._resizeObserver.disconnect();
        this._players.forEach(p => { this.scene.remove(p.root); p.dispose(); });
        this._players = [];
        this._clearPathsAndArrows();
        this.factory.dispose();
        this.arena.dispose();
        [this._puck, this._halo, this._trailMesh, this._ring].forEach(m => { m.geometry.dispose(); m.material.dispose(); });
        this._glowTex.dispose();
        this._envTex.dispose();
        this.director.dispose();
        this.fx.dispose();
        this.renderer.dispose();
        if (this.renderer.domElement.parentNode) {
            this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
    }
}
